export type TUser = {
  email: string;
};

export enum DocumentGroups {
  CompanyDocuments = 'Company Documents',
  LegalDocuments = 'Legal Documents',
  AdditionalDocuments = 'Additional Documents',
  ClientProvidedDocuments = 'Client Provided Documents',
}

export enum PaymentMethod {
  BankTransfer = 'Bank Transfer',
  Cryptocurrency = 'Cryptocurrency',
}

export enum DocumentType {
  CorporateDocuments = 1,
  Accounting,
  Miscellaneous,
  AMLServices,
  TravelRule,
  Renewals,
  AccountingDocuments,
  ActOfTheReportingPersons,
  Web3,
  ReportingDocuments,
  MICA,
  ProofOfAddress,
  AMLDocuments,
  PrivacyPolicy,
  LegalOpinions,
  GDPR,
  TermsOfService,
  Unsorted,
  KYC,
  PaymentConfirmation,
}

type SubItem = {
  name: string;
  renewalDate: string;
  units: string;
  price: string;
  currency: string;
};

type Invoice = {
  name: string;
  issueDate: string;
  href: string;
};

export enum InvoiceStatus {
  ISSUED = 'issued',
  CONFIRMED_BY_CLIENT = 'confirmed-by-client',
  PAID = 'paid',
  PARTIALLY_PAID = 'partially-paid',
  CANCELLED = 'cancelled',
}

export interface IndividualClientDetails {
  fullName: string;
  documentNumber: string;
  nationality: string;
  residenceCountry: string;
  legalAddress: string;
}

export interface CorporateClientDetails {
  companyName: string;
  companyRegNumber: string;
  companyTaxId: string;
  companyLegalAddress: string;
}

export interface PaymentDetails {
  paymentMethod: PaymentMethod | string;
}

export enum ClientType {
  Individual = 'Individual',
  Corporate = 'Corporate',
}

export interface ClientInput {
  clientType: ClientType;
  details: IndividualClientDetails | CorporateClientDetails;
  payment: PaymentDetails;
}

export type InstallmentGroupedService = {
  i: string;
  description: string;
  isChild: boolean;
  group: string;
  cpGroup: string | null;
  groupHead: string | null;
};

export type InstallmentGroup = {
  seq: number | null;
  label: string;
  amount: number | null;
  currency: string | null;
  note: string | null;
  isCurrent: boolean;
  services: InstallmentGroupedService[];
};

export type InstallmentContext = {
  groups: InstallmentGroup[];
  seq: number;
  total: number;
};

/**
 * Every figure a surface may state about an invoice, resolved by the backend
 * so the page, the PDF and the list cannot disagree. Never recompute these.
 */
export type TInvoiceMoney = {
  /** The invoice as issued, before any discount. */
  preDiscountTotal: number;
  discountPercentage: number | null;
  /** What the discount takes off the subtotal. */
  discountAmount: number;
  /** What the discount takes off the total, tax included. */
  totalReduction: number;
  /** The tax actually charged, which follows the discounted subtotal. */
  taxAmount: number;
  /** What the client owes, in the document currency. */
  payable: number;
  /** What the client owes, in the currency they settle in. */
  amountDue: number;
};

export type InvoiceExtendedFields = {
  clientInformation?: {
    type: 'individual' | 'corporate';
    name: string;
    email: string;
    address?: string;
    taxId?: string;
  };
  invoicePositions?: {
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }[];
  paymentConfirmation?: {
    transactionHash?: string;
    fileId?: string;
    fileName?: string;
    updatedAt?: string;
    updatedBy?: string;
  };
  /** Every movement recorded against the invoice, oldest first. */
  payments?: {
    id: string;
    amount: number;
    collectedAt: string;
    recordedAt: string;
    recordedByEmail: string | null;
  }[];
  /** The movement count a new entry must be submitted against. */
  paymentSeq?: number;
  isOverdue?: boolean;
  createdBy?: string;
  updatedAt?: string;
  documentUrl?: string;
  notes?: string;
  coverageItemIds: string[];
  /** The plan laid out installment by installment, exactly as the customer's
   *  document reads it. Absent on ordinary (non-split) invoices. */
  installments?: InstallmentContext | null;
  accountId: number | null;
  invoiceRecipient?: {
    email: string;
    billToName?: string;
    kind: 'payer';
    blocked: boolean;
    blockReason?: string;
  };
  latestEmail?: {
    id: number;
    status: EmailDeliveryStatus | null;
    statusAt: string | null;
    error: string | null;
    to: string | null;
  } | null;
  /** Which document version the client is holding, if any. */
  delivery?: DeliveryState;
  deliveries?: DeliveryRecord[];
  amendments?: AmendmentRecord[];
  documentVersion?: number;
  /** Set when an accountant stated the VAT rate by hand; also the audit reason. */
  vatOverrideReason?: string | null;
};

export type DeliveryStateName = 'never_sent' | 'sent' | 'outdated_at_client';

export type DeliveryState = {
  state: DeliveryStateName;
  clientHasCurrentVersion: boolean;
  sentVersion: number | null;
  sentTo: string | null;
  sentAt: string | null;
};

export type DeliveryRecord = {
  id: number;
  documentVersion: number;
  recipientEmail: string;
  message: string | null;
  succeeded: boolean;
  error: string | null;
  sentAt: string;
  sentByName: string | null;
};

export type AmendmentRecord = {
  id: number;
  changedAt: string;
  changedByName: string | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
};

export type EmailDeliveryStatus =
  | 'queued'
  | 'submission_unknown'
  | 'accepted'
  | 'delivered'
  | 'opened'
  | 'bounced'
  | 'complained'
  | 'unsubscribed'
  | 'failed';

export enum TInvoiceOpportunityStatus {
  Created = 'created',
  Queued = 'queued',
  Processing = 'processing',
  Failed = 'failed',
}

export type DiscountInfo = {
  id: number;
  discountPercentage: number;
  newDueAmount: number;
  appliedBy: number;
  appliedByEmail: string | null;
  appliedAt: string;
  notes: string | null;
};

export type DiscountHistoryEntry = {
  id: number;
  discountPercentage: number;
  newDueAmount: number;
  appliedAt: string;
  appliedByEmail: string | null;
  notes: string | null;
};

export type TInvoice = {
  id: number;
  invoiceNumber: string;
  isTest?: boolean;
  status: InvoiceStatus;
  dueAmount: number;
  dueCurrency: string;
  paidAmount: number;
  createdAt: string;
  dueAt: string;
  paidAt: string | null;
  opportunityId: number | null;
  opportunityStatus: TInvoiceOpportunityStatus | null;
  paymentConfirmationHash: string | null;
  paymentConfirmationFileId: number | null;
  paymentConfirmationUpdatedAt: string | null;
  paymentConfirmationUpdatedBy: string | null;
  statusUpdatedAt: string;
  statusUpdatedBy: string | null;
  paymentMethod: PaymentMethod | string;
  contactName: string;
  contactEmail: string;
  contactId: number;
  offerId: number | null;
  offerTitle: string | null;
  offerUuid: string;
  clientInput?: ClientInput;
  proposalName: string;
  jurisdiction?: string;
  remainingAmount: number;
  renewalId: number | null;
  isAggregated: boolean;
  aggregationLabel: string | null;
  payerId: number | null;
  partNumber: number | null;
  invoicingCompanyId: number | null;
  invoicingCompanyName: string | null;
  baseCurrency: string;
  /** Resolved by the backend; formatted, never recomputed, by any surface. */
  money?: TInvoiceMoney;
  /** How this invoice states tax, decided by the backend from the issuer frozen
   *  on it and never from the rate, so the page and the document agree. Absent
   *  on invoices read from a list payload. */
  taxLine?: { showRate: boolean; showDash: boolean };
  /** The entity that issued the invoice, frozen at issuance. */
  issuer?: {
    name: string;
    countryCode: string;
    registrationNumber: string | null;
    vatNumber: string | null;
    vatRegime: 'standard' | 'none';
  } | null;
  subtotalAmount?: number;
  vatRate?: number;
  vatAmount?: number;
  vatNote?: string | null;
  totalAmount?: number;
  dueConversionRate?: number;
  offRampingFee?: number;
  positions?: {
    id: number;
    blockTitle: string;
    serviceName: string;
    serviceId: number;
    priceAmount: number;
    priceCurrency: string;
    weight: number;
    quantity: number;
    isCustom: boolean;
    serviceParentId: number | null;
  }[];
  statusHistory?: {
    oldStatus: InvoiceStatus;
    newStatus: InvoiceStatus;
    changedAt: string;
    notes?: string;
    changedByEmail?: string;
  }[];
  discount?: DiscountInfo | null;
  discountHistory?: DiscountHistoryEntry[];
  originalDueAmount: number;
};

// Search and filter types for invoice management
export interface InvoiceSearchFilters {
  invoiceNumber?: string;
  clientName?: string;
  clientEmail?: string;
  amount?: number;
  currency?: string;
  status?: InvoiceStatus;
  dateFrom?: Date;
  dateTo?: Date;
  source?: 'offer' | 'manual' | 'all';
}

export type InvoiceSortField =
  | 'created_at'
  | 'due_at'
  | 'total_amount'
  | 'status';

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  sortBy?: InvoiceSortField;
  sortOrder?: 'asc' | 'desc';
  status?: InvoiceStatus | 'all';
  filters?: InvoiceSearchFilters;
}

export interface InvoiceListResponse {
  invoices: TInvoice[];
  total: string;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, string>;
}

// Service list types
export type ServiceSortField =
  | 'name'
  | 'updatedAt'
  | 'price'
  | 'type'
  | 'childrenCount';
export type ServiceFilter = 'packages' | 'standalone';

export interface ServiceListParams {
  page?: number;
  limit?: number;
  sortBy?: ServiceSortField;
  sortOrder?: 'asc' | 'desc';
  term?: string;
  jurisdiction?: string;
  strictJurisdiction?: boolean;
  currency?: string;
  type?: string;
  minPrice?: number;
  maxPrice?: number;
  exclude?: number[];
  serviceFilter?: ServiceFilter;
}

// Commercial Proposal list types
export type CPListSortField =
  | 'name'
  | 'updatedAt'
  | 'jurisdiction'
  | 'currency';

export interface CPListParams {
  page?: number;
  limit?: number;
  sortBy?: CPListSortField;
  sortOrder?: 'asc' | 'desc';
  term?: string;
  jurisdiction?: string;
  currency?: string;
  isDraft?: boolean;
  shopEnabled?: boolean;
  /** Comma-separated substrings; a proposal must carry a block title matching each. */
  blockTitle?: string;
  /** Proposal must link every one of these services. */
  serviceIds?: number[];
}

// Admin Users list types
export interface AdminUsersListParams {
  page?: number;
  limit?: number;
}

export interface InvoiceStatusUpdateRequest {
  id: string | number;
  status: InvoiceStatus;
  amount?: number;
  notes?: string;
}

export interface InvoicePaymentConfirmationRequest {
  id: string | number;
  transactionHash?: string;
  fileId?: string;
  notes?: string;
}

export type Document = {
  id: number;
  filename: string;
  createdBy: string;
  createdAt: string;
  typeName: string;
  typeId: number;
  hidden: boolean;
  name: string;
  deleted: boolean;
};

export type DocumentTypeInfo = {
  id: number;
  name: string;
  description?: string;
  groupName: string;
  groupDescription?: string;
};

export type DocumentGroup = {
  id: number;
  name: string;
  description?: string;
};

export type TCompany = {
  readonly id: number;
  name: string;
  regNumber: string;
  regDate: string;
  regAddress: string;
  vat: string;
  jurisdiction: string;
  subitems: SubItem[];
  invoices: Invoice[];
  documents?: Document[];
};

export type Me = {
  id: string;
  email: string;
  info: {
    name: string;
    picture: string;
  };
  uuid: string;
  created_at: string;
  monday_token?: string;
};

interface ColumnValue {
  id: string;
  text: string;
}

export type MondayItem = {
  id: string;
  name: string;
  column_values: ColumnValue[];
};

// Helper function to determine if payment confirmation is required based on payment method
export const isPaymentConfirmationRequired = (
  paymentMethod: PaymentMethod | string,
): boolean => {
  return paymentMethod === PaymentMethod.Cryptocurrency;
};
